import React from "react";
import { PopoverButton } from "@headlessui/react";
import { strings } from "../../resources/strings";

export interface WizardToolbarProps {
  /** Close/exit the wizard (dismisses the host modal). */
  onClose: () => void;
  /** Disable the Question List trigger when there are no questions to list. */
  questionListDisabled?: boolean;
}

/**
 * Persistent toolbar chrome for the Report Form wizard, shown on every screen.
 *
 * Layout (Figma node 207:7629): Question List button (left), "Report Form"
 * title (center), close button (right). All controls meet the 44px minimum
 * touch-target size for iPad / mobile browsers.
 *
 * The Question List button is a `PopoverButton` (IHA-387) — the host
 * (`NewQuestionsForm`) wraps this toolbar + the menu panel in one `<Popover>` so
 * the panel anchors to this button. Must be rendered within a `<Popover>`.
 */
const WizardToolbar: React.FC<WizardToolbarProps> = ({
  onClose,
  questionListDisabled,
}) => {
  return (
    <div
      data-testid="wizard-toolbar"
      className="grid w-full shrink-0 grid-cols-[auto_1fr_auto] items-center"
    >
      {/* Left — Question List: opens the anchored menu popover (IHA-387). While
          open it is lifted above the menu's dark backdrop (z-[60] > the
          backdrop's z-[55]) so it stays lit while the rest of the wizard dims —
          that contrast is the highlight, so no color change is needed. */}
      <PopoverButton
        disabled={questionListDisabled}
        aria-label={strings.reportForm.questionList}
        className={({ open }) =>
          `relative flex items-center gap-1 min-h-[44px] min-w-[44px] px-3 rounded-lg border border-primary bg-[#FCFCFC] text-primary text-xs shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${
            open ? "z-[60]" : ""
          }`
        }
      >
        <span aria-hidden="true" className="text-base leading-none">
          ☰
        </span>
        <span>{strings.reportForm.questionList}</span>
      </PopoverButton>

      {/* Center — title. In the middle grid cell (truncates rather than
          overlapping the ☰ / ✕ controls on narrow iPad panels, IHA-350). */}
      <h2 className="min-w-0 truncate px-2 text-center text-[17px] font-semibold text-gray-900">
        {strings.reportForm.title}
      </h2>

      {/* Right — close */}
      <button
        type="button"
        onClick={onClose}
        aria-label={strings.reportForm.closeLabel}
        className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full text-2xl leading-none text-gray-500 hover:text-gray-700"
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
};

export default WizardToolbar;
